import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  LinearProgress,
  Switch,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { withTranslation, WithTranslation } from 'react-i18next';
import ParticipantScopes from '@constants/scopes';
import { useMytaverse } from '@providers/MytaverseProvider';

import { useAssistant } from '@core/assistant/providers/Provider';
import { SCREENS } from '@core/assistant/interfaces';
import { useGreetings } from '@core/assistant/providers/Greetings';
import { log } from '@core/assistant/providers/Debug';

import DebugOverlay from './DebugOverlay';
import QuizScreen from '../QuizScreen/QuizScreen';
import { useStyles } from './styles';

const DefaultScreen = ({ t: translate }: WithTranslation) => {
  const { user } = useMytaverse();
  const { generateGreetingsAudio } = useGreetings();
  const classes = useStyles();

  const [showDebugUI, setShowDebugUI] = useState(false);
  const [showScriptQuiz, setShowScriptQuiz] = useState(false);
  const [showNextButton, setShowNextButtton] = useState(false);
  const intervalTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const isScriptTransitioningRef = useRef(false);

  const {
    navigate,
    setOpen,
    setNavigationHistory,
    stopPlayback,
    audioPromptHistory,
    startFlow,
    linearScriptPlayerRef,
    isAudioPlaybackActive,
    audioEndTick,
    isScriptCompleted,
    setIsScriptCompleted,
    canUserSkip,
    pendingScriptStart,
    confirmScriptStart,
    fetchScripts,
    fetchParticipantProgress,
    resetProgress,
    saveQuizAnswers,
    clearPrerenderedAudio,
    setIsScriptQuizActive,
    simulateRegionChange,
    isScriptTransitioning,
  } = useAssistant();

  useEffect(() => {
    setIsScriptQuizActive(showScriptQuiz);
  }, [showScriptQuiz]);

  const handleNext = () => {
    setShowNextButtton(false);
    linearScriptPlayerRef.current?.triggerNext();
  };

  const handleSkip = () => {
    linearScriptPlayerRef.current?.stop();
    setShowScriptQuiz(false);
    stopPlayback();
  };

  const handleHide = () => {
    stopPlayback();
    setOpen(false);
    setNavigationHistory([]);
    navigate(SCREENS.DEFAULT);
  };

  const handleScriptQuizConfirm = (selectedOptions: number[]) => {
    saveQuizAnswers(selectedOptions);
    setShowScriptQuiz(false);
    linearScriptPlayerRef.current?.triggerNext();
  };

  useEffect(() => {
    log('audioPromptHistory', audioPromptHistory);
    log(
      'new item in audioPromptHistory',
      audioPromptHistory[audioPromptHistory.length - 1],
    );
  }, [audioPromptHistory]);

  useEffect(() => {
    if (isScriptTransitioningRef.current) {
      setShowNextButtton(false);
      return;
    }

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();
    log('linearScriptPlayerState', linearScriptPlayerState);

    const currentNode = linearScriptPlayerState?.currentNode;

    if (currentNode?.questionary?.type === 'multiple_choice') {
      setShowScriptQuiz(true);
      setShowNextButtton(false);
    } else if (!isAudioPlaybackActive && currentNode?.awaitButtonNext) {
      setShowNextButtton(true);
    } else if (isAudioPlaybackActive && currentNode?.awaitButtonNext) {
      setShowNextButtton(false);
    } else if (!currentNode?.awaitButtonNext) {
      setShowNextButtton(false);
    } else if (linearScriptPlayerState?.isCompleted) {
      setShowNextButtton(false);
    }
  }, [isAudioPlaybackActive, audioEndTick]);

  // dirty fix to disable Next button when script is completed
  useEffect(() => {
    intervalTimer.current = setInterval(() => {
      const linearScriptPlayerState =
        linearScriptPlayerRef.current?.getCurrentState();

      if (!linearScriptPlayerState?.isRunning) {
        setShowNextButtton(false);
      }
    }, 1000);
  }, []);

  const scriptPlayerState = linearScriptPlayerRef.current?.getCurrentState();
  const questionaryInfo = linearScriptPlayerRef.current?.getQuestionaryInfo();
  const maxOptions = linearScriptPlayerRef.current?.getMaxOptionsCount() ?? 0;
  const activeQuestionary = scriptPlayerState?.currentNode?.questionary;
  const activeQuestionText = scriptPlayerState?.currentNode?.text;
  const progressInfo = linearScriptPlayerRef.current?.getProgressInfo();
  const isScriptRunning = scriptPlayerState?.isRunning ?? false;
  isScriptTransitioningRef.current = isScriptTransitioning;

  const isDebugVisible = user?.scopes?.includes(
    ParticipantScopes.ViewAssistantDebug,
  );

  return (
    <Box sx={classes.box}>
      {isScriptRunning && progressInfo && progressInfo.totalSteps > 1 && (
        <Box sx={classes.progressSection}>
          <LinearProgress
            variant="determinate"
            value={progressInfo.percentage}
            sx={classes.progressBar}
          />
          <Box sx={classes.progressLabels}>
            <Typography sx={classes.progressText}>
              {translate('assistant.stepOf', {
                currentStep: progressInfo.currentStep,
                totalSteps: progressInfo.totalSteps,
              })}
            </Typography>
            <Typography sx={classes.progressText}>
              {progressInfo.percentage}%
            </Typography>
          </Box>
        </Box>
      )}

      <Collapse in={isScriptRunning && showNextButton} unmountOnExit>
        <Button
          fullWidth
          sx={{ ...classes.button, ...classes.blueButton, mt: 1 }}
          onClick={() => handleNext()}
          endIcon={<ArrowForwardIcon />}
        >
          {translate('next')}
        </Button>
      </Collapse>

      <Collapse in={!!pendingScriptStart && !isScriptCompleted} unmountOnExit>
        <Button
          fullWidth
          sx={{ ...classes.button, ...classes.blueButton }}
          onClick={() => confirmScriptStart()}
          endIcon={<ArrowForwardIcon />}
        >
          {pendingScriptStart?.title}
        </Button>
      </Collapse>

      {(isScriptRunning || isScriptTransitioning || !!pendingScriptStart) &&
        !isScriptCompleted && (
          <Box sx={classes.scriptActiveCard}>
            <Typography sx={classes.scriptActiveCardTitle}>
              {translate('assistant.askMe.haveQuestion')}
            </Typography>
            <Button
              fullWidth
              sx={classes.askButton}
              onClick={() => navigate(SCREENS.CHAT)}
              endIcon={
                <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />
              }
            >
              {translate('assistant.askMe.title')}
            </Button>
          </Box>
        )}

      {isScriptCompleted && (
        <Box sx={classes.completedBox}>
          <Box sx={classes.completedIconCircle}>
            <CheckIcon sx={{ color: '#4caf50', fontSize: 28 }} />
          </Box>
          <Typography sx={classes.completedTitle}>
            {translate('assistant.scriptCompleted')}
          </Typography>
          <Typography sx={classes.completedSubtitle}>
            {translate('assistant.allStepsDone')}
          </Typography>
          <Button
            fullWidth
            sx={{ ...classes.button, ...classes.blueButton }}
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              setIsScriptCompleted(false);
              stopPlayback();
              setOpen(false);
              setNavigationHistory([]);
              navigate(SCREENS.DEFAULT);
            }}
          >
            {translate('assistant.continue')}
          </Button>
        </Box>
      )}

      {!isScriptRunning &&
        !isScriptCompleted &&
        !pendingScriptStart &&
        !isScriptTransitioning && (
          <Button
            fullWidth
            sx={{ ...classes.button, ...classes.blueButton }}
            onClick={() => navigate(SCREENS.CHAT)}
          >
            {translate('assistant.askMe.title')}
          </Button>
        )}

      {isDebugVisible && (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={showDebugUI}
                onChange={() => setShowDebugUI(!showDebugUI)}
              />
            }
            sx={{
              color: '#fff',
            }}
            label={translate('assistant.showDebug')}
          />
        </Box>
      )}

      {isScriptRunning || isScriptTransitioning ? (
        <Box sx={classes.bottomRow}>
          <Collapse in={canUserSkip} orientation="horizontal">
            <Button sx={classes.textLink} onClick={handleSkip}>
              {translate('assistant.skipScript')}
            </Button>
          </Collapse>
          <Button sx={{ ...classes.textLink, ml: 'auto' }} onClick={handleHide}>
            {translate('assistant.hide')}
          </Button>
        </Box>
      ) : (
        <Button sx={classes.hideButton} onClick={handleHide}>
          {translate('assistant.hideNoa')}
        </Button>
      )}

      {showScriptQuiz && activeQuestionary && activeQuestionText && (
        <QuizScreen
          questionText={activeQuestionText}
          questionary={activeQuestionary}
          questionNumber={questionaryInfo?.current}
          totalQuestions={questionaryInfo?.total}
          maxOptions={maxOptions}
          onConfirm={handleScriptQuizConfirm}
        />
      )}

      {showDebugUI && (
        <DebugOverlay
          startFlow={(flowId) => {
            setShowDebugUI(false);
            startFlow(flowId);
          }}
          stopPlayer={() => {
            linearScriptPlayerRef.current?.stop();
            setShowScriptQuiz(false);
          }}
          generateGreetingsAudio={generateGreetingsAudio}
          fetchScripts={fetchScripts}
          fetchParticipantProgress={fetchParticipantProgress}
          resetProgress={resetProgress}
          clearPrerenderedAudio={clearPrerenderedAudio}
          simulateRegionChange={simulateRegionChange}
        />
      )}
    </Box>
  );
};

export default withTranslation('common')(DefaultScreen);
